import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import TimePicker24 from "./TimePicker24";
import { Clock, LogIn, LogOut, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Modale de pointage manuel (admin/manager) avec heure modifiable.
 *
 * Props :
 * - mode : "in" | "out"
 * - target :
 *    { shift_id, collaborateur_name, planned_start, planned_end }   (mode "in")
 *    { entry_id, collaborateur_name, clock_in, planned_end }        (mode "out")
 * - onSuccess() : callback après pointage réussi (close + refresh)
 */
export default function ManualClockDialog({
  open, onOpenChange, mode, target, onSuccess,
}) {
  const isIn = mode === "in";
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !target) return;
    // Pré-remplir avec l'heure planifiée (start_time pour entrée, end_time pour sortie)
    setTime(isIn ? (target.planned_start || "") : (target.planned_end || ""));
  }, [open, target, isIn]);

  const submit = async () => {
    if (!target) return;
    const re = /^\d{2}:\d{2}$/;
    if (!re.test(time)) {
      toast.error("Format d'heure attendu : HH:MM");
      return;
    }
    setBusy(true);
    try {
      if (isIn) {
        await api.post(
          `/pointage/late-arrivals/${target.shift_id}/clock-in`,
          { clock_in_time: time },
        );
        toast.success(`Entrée pointée à ${time} pour ${target.collaborateur_name}`);
      } else {
        await api.post(
          `/timeentries/${target.entry_id}/manual-clock-out`,
          { clock_out_time: time },
        );
        toast.success(`Sortie pointée à ${time} pour ${target.collaborateur_name}`);
      }
      onOpenChange?.(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du pointage");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="manual-clock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isIn ? <LogIn size={18} className="text-blue-700" /> : <LogOut size={18} className="text-emerald-700" />}
            {isIn ? "Pointer l'entrée du collaborateur" : "Pointer la sortie du collaborateur"}
          </DialogTitle>
          <DialogDescription>
            <span className="block">
              <span className="text-zinc-900 font-medium">{target?.collaborateur_name || "—"}</span>
            </span>
            <span className="block mt-1 text-[11px] font-mono text-zinc-500">
              {isIn
                ? `Planifié : ${target?.planned_start || "—"} → ${target?.planned_end || "—"}`
                : `Entrée pointée : ${target?.clock_in || "—"} · planifié → ${target?.planned_end || "—"}`}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label className="label-uppercase mb-1.5 block" htmlFor="manual-clock-time">
              <span className="inline-flex items-center gap-1.5">
                <Clock size={11} />
                {isIn ? "Heure d'entrée à valider" : "Heure de sortie à valider"}
              </span>
            </Label>
            <TimePicker24
              id="manual-clock-time"
              value={time}
              onChange={(v) => setTime(v)}
              className="font-mono"
              data-testid="manual-clock-time-input"
            />
            <p className="mt-1.5 text-[11px] text-zinc-500 font-mono inline-flex items-center gap-1.5">
              <AlertCircle size={11} />
              Ajustez l'heure réellement constatée. Action tracée dans l'historique de sécurité.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="manual-clock-cancel"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !time}
            className={isIn ? "bg-blue-700 hover:bg-blue-800 text-white" : "bg-emerald-700 hover:bg-emerald-800 text-white"}
            data-testid="manual-clock-submit"
          >
            {busy ? "…" : (isIn ? "Valider l'entrée" : "Valider la sortie")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
